import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";
import type { RelatorioConsumoLinha } from "../../../services/controleProducaoService";
import { groupByUndNome } from "./groupByUnd";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(inicio: string, fim: string): string {
  return `${inicio}_${fim}`;
}

function formatNum(n: number | null | undefined, und?: string): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const txt = Number.isInteger(n)
    ? n.toLocaleString("pt-BR")
    : n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
  return und ? `${txt} ${und}` : txt;
}

function formatBase(base: string | null | undefined, und?: string): string {
  const txt = base?.trim();
  if (!txt) return "—";
  return und ? `${txt} ${und}` : txt;
}

export type DetalhamentoFiltroResumo = { label: string; value: string };

export interface DetalhamentoExportInput {
  lojaNome: string;
  periodoInicioISO: string;
  periodoFimISO: string;
  filtros: DetalhamentoFiltroResumo[];
  linhas: RelatorioConsumoLinha[];
  geradoEmLabel: string;
}

const DETALHE_HEAD = [
  "Unidade",
  "Data",
  "Dia",
  "Item",
  "Base",
  "Est. inicial",
  "Produção",
  "Sobra",
  "Consumo",
];

function detalheRows(linhas: RelatorioConsumoLinha[]): string[][] {
  return groupByUndNome(linhas, (l) => l.undNome).flatMap((grupo) =>
    grupo.items.map((l) => [
      l.undNome,
      l.periodoLabel,
      l.diaSemanaLabel ?? "—",
      l.descricao,
      formatBase(l.base, l.undNome),
      formatNum(l.estoqueInicial, l.undNome),
      formatNum(l.producao, l.undNome),
      formatNum(l.sobra, l.undNome),
      formatNum(l.consumo, l.undNome),
    ])
  );
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportDetalhamentoXlsx(input: DetalhamentoExportInput): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Detalhamento — Consumo e Produção"],
    [],
    ["Período do relatório", `${pIni} a ${pFim}`],
    ["Loja", input.lojaNome],
    ...input.filtros.map((f) => [f.label, f.value]),
    ["Total de linhas", String(input.linhas.length)],
    [],
    ["Gerado em", input.geradoEmLabel],
    [],
    DETALHE_HEAD,
    ...detalheRows(input.linhas),
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 28 },
    { wch: 10 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 12 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Detalhamento");
  XLSX.writeFile(
    wb,
    `detalhamento-consumo-${fileDateSuffix(input.periodoInicioISO, input.periodoFimISO)}.xlsx`
  );
}

export function exportDetalhamentoPdf(input: DetalhamentoExportInput): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const margin = 14;
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const docExt = doc as JsPdfWithAutoTable;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Detalhamento diário", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Consumo e produção", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni} até ${pFim}`],
      ["Loja", input.lojaNome],
      ...input.filtros.map((f) => [f.label, f.value]),
      ["Linhas exportadas", String(input.linhas.length)],
      ["Gerado em", input.geradoEmLabel],
    ],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: [42, 42, 42] as [number, number, number],
      cellPadding: 2.5,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 42 },
      1: { cellWidth: pageW - margin * 2 - 42 },
    },
    margin: { left: margin, right: margin },
  });

  const tableStyles = {
    theme: "striped" as const,
    headStyles: {
      fillColor: primary,
      textColor: 255 as const,
      fontStyle: "bold" as const,
      halign: "left" as const,
      valign: "middle" as const,
    },
    bodyStyles: {
      textColor: [38, 38, 38] as [number, number, number],
      lineColor: [230, 230, 230] as [number, number, number],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251] as [number, number, number],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 7,
      cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1.5 },
      lineWidth: 0.08,
    },
  };

  let y = (docExt.lastAutoTable?.finalY ?? 40) + 6;
  for (const grupo of groupByUndNome(input.linhas, (l) => l.undNome)) {
    if (y > pageH - 40) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(42, 42, 42);
    doc.text(`Unidade ${grupo.undNome}`, margin, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [DETALHE_HEAD],
      body: detalheRows(grupo.items),
      ...tableStyles,
    });
    y = (docExt.lastAutoTable?.finalY ?? y) + 8;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(
    `detalhamento-consumo-${fileDateSuffix(input.periodoInicioISO, input.periodoFimISO)}.pdf`
  );
}
